/**
 * OrbitPrint — the four account pages: sign in, create account, forgot, reset.
 * Small enough to live together; each is a thin form over OrbitAuthContext.
 */
import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

/** An invite token riding along in the URL: after sign-in / register, finish accepting it. */
function useInviteRedirect() {
  const [params] = useSearchParams();
  const p = useOrbitPath();
  const invite = params.get('invite') || '';
  return { invite, prefillEmail: params.get('email') || '', after: invite ? `${p('/accept-invite')}?token=${encodeURIComponent(invite)}` : p('/'), q: invite ? `?invite=${encodeURIComponent(invite)}` : '' };
}
import { toast } from 'sonner';
import { useOrbitAuth, useOrbitPath, orbitApi, errText } from '../OrbitAuthContext';
import { AuthShell, OrbitInput, OrbitButton, ArrowIcon } from '../components/OrbitShell';

function useRedirectIfSignedIn() {
  const { user, loading } = useOrbitAuth();
  const { after } = useInviteRedirect();
  if (!loading && user) return <Navigate to={after} replace />;
  return null;
}

export function OrbitLoginPage() {
  const redirect = useRedirectIfSignedIn();
  const { login } = useOrbitAuth();
  const p = useOrbitPath();
  const navigate = useNavigate();
  const { after, q, prefillEmail } = useInviteRedirect();
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  if (redirect) return redirect;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      navigate(after);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      lead="Your locations and printers are waiting."
      footer={<span>New to OrbitPrint? <Link to={`${p('/register')}${q}`} className="orbit-link" data-testid="orbit-go-register">Create an account</Link></span>}
    >
      <form onSubmit={submit} noValidate>
        <OrbitInput label="Email" type="email" name="email" autoComplete="email" testid="orbit-login-email"
          value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" />
        <OrbitInput label="Password" type="password" name="password" autoComplete="current-password" testid="orbit-login-password"
          value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Your password" />
        <div className="flex justify-end -mt-1 mb-5">
          <Link to={p('/forgot-password')} className="text-xs orbit-link is-muted" data-testid="orbit-forgot-link">Forgot password?</Link>
        </div>
        <OrbitButton type="submit" block busy={busy} testid="orbit-login-submit">Sign in <ArrowIcon /></OrbitButton>
      </form>
    </AuthShell>
  );
}

export function OrbitRegisterPage() {
  const redirect = useRedirectIfSignedIn();
  const { register } = useOrbitAuth();
  const p = useOrbitPath();
  const navigate = useNavigate();
  const { after, q, prefillEmail } = useInviteRedirect();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  if (redirect) return redirect;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setBusy(true);
    try {
      await register(email, password, fullName);
      toast.success('Account created');
      navigate(after);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      lead="Free to start. One account covers every printer you own, at every location."
      footer={<span>Already have one? <Link to={`${p('/login')}${q}`} className="orbit-link" data-testid="orbit-go-login">Sign in</Link></span>}
    >
      <form onSubmit={submit} noValidate>
        <OrbitInput label="Your name" name="full_name" autoComplete="name" testid="orbit-register-name"
          value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jan" />
        <OrbitInput label="Email" type="email" name="email" autoComplete="email" testid="orbit-register-email"
          value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" />
        <OrbitInput label="Password" type="password" name="password" autoComplete="new-password" testid="orbit-register-password"
          value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 8 characters"
          hint="At least 8 characters." />
        <OrbitButton type="submit" block busy={busy} testid="orbit-register-submit">Create account <ArrowIcon /></OrbitButton>
      </form>
    </AuthShell>
  );
}

export function OrbitForgotPasswordPage() {
  const p = useOrbitPath();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await orbitApi.post('/auth/forgot-password', { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      lead={sent ? undefined : "Enter your email and we'll send a link to choose a new password."}
      footer={<span><Link to={p('/login')} className="orbit-link" data-testid="orbit-back-to-login">Back to sign in</Link></span>}
    >
      {sent ? (
        <div className="orbit-panel p-5" data-testid="orbit-forgot-sent">
          <p className="font-semibold">Check your inbox</p>
          <p className="mt-1 text-sm orbit-ink2">
            If <span className="orbit-mono">{email}</span> has an OrbitPrint account, a reset link is on its way. It works for one hour.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <OrbitInput label="Email" type="email" name="email" autoComplete="email" testid="orbit-forgot-email"
            value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" />
          <OrbitButton type="submit" block busy={busy} testid="orbit-forgot-submit">Send reset link</OrbitButton>
        </form>
      )}
    </AuthShell>
  );
}

export function OrbitResetPasswordPage() {
  const p = useOrbitPath();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await orbitApi.post('/auth/reset-password', { token, new_password: password });
      toast.success('Password reset. Sign in with your new password.');
      navigate(p('/login'));
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Reset link missing" lead="Open the link from your email, or request a new one."
        footer={<Link to={p('/forgot-password')} className="orbit-link">Request a new link</Link>}>
        <div />
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password"
      footer={<span><Link to={p('/login')} className="orbit-link">Back to sign in</Link></span>}>
      <form onSubmit={submit} noValidate>
        <OrbitInput label="New password" type="password" name="new_password" autoComplete="new-password" testid="orbit-reset-password"
          value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 8 characters" />
        <OrbitInput label="Confirm new password" type="password" name="confirm" autoComplete="new-password" testid="orbit-reset-confirm"
          value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Type it again" />
        <OrbitButton type="submit" block busy={busy} testid="orbit-reset-submit">Save new password</OrbitButton>
      </form>
    </AuthShell>
  );
}
